// API client. Replaces storage.js's localStorage reads/writes with fetch
// calls to the Hono Worker. Same exported function names where it makes
// sense (loadTasks / saveSession / loadSession) so the rest of the app
// barely notices the cutover.
//
// Auth: server returns an opaque token from /auth/login; we keep it in
// localStorage under `vava_token`. Every other call sends it as
// `Authorization: Bearer <token>`. /auth/logout deletes the row.
//
// Env: VITE_API_URL points at the Worker. Defaults to localhost:8787
// for `wrangler dev`.

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8787';
const TOKEN_KEY = 'vava_token';
const STAFF_KEY = 'vava_staff';                 // cached staff record from /auth/login

export type AccountState = 'invited' | 'active' | 'deactivated';

export interface Staff {
  id: string;
  name: string;
  role: 'admin' | 'store_manager' | 'staff';
  stores: string[];
  title: string;
  email: string | null;
  active: boolean;
  state: AccountState;
  canManageUsers: boolean;
  superAdmin: boolean;                // 超级管理员 (Regina) — HR restricted-profile access
  shift: 'morning' | 'evening' | null;  // 领班's led shift; null = not a 领班
  profilePicKey: string | null;
  dept: string | null;
}

/** Build the public URL for a staff member's profile picture. The image
 * endpoint returns 404 if no pic is set; callers can render a fallback
 * (the monogram in Avatar.jsx) when the request fails. */
export function profilePicUrl(staffId: string, cacheKey?: string | null): string {
  const v = cacheKey ? `?v=${encodeURIComponent(cacheKey)}` : '';
  return `${API_BASE}/staff/${encodeURIComponent(staffId)}/profile-pic${v}`;
}

export interface Owner {
  staffId: string;
  role: 'R' | 'A' | 'C' | 'I';
  isPrimary?: boolean;            // true on the 主负责人 (R only)
}

// Frontend-shape Task. The Worker returns camelCase + an extra
// assigneeId field (= first R owner) so the existing React components
// keep working unchanged. SQL columns stay snake_case; the API layer
// maps at the boundary (worker/src/index.ts fetchTask).
export interface Task {
  id: string;
  title: string;
  detail: string | null;                        // legacy alias for description
  description: string | null;
  type: 'general' | 'training';
  status: '待开始' | '进行中' | '阻塞' | '已完成' | '已取消';
  priority: 'P0' | 'P1' | 'P2';
  category: string | null;                      // legacy free-text; superseded by groupId
  groupId: string | null;                       // FK to groups.id (since 0007)
  dueDate: string | null;
  startDate: string | null;
  completedAt: string | null;
  projectId: string | null;
  parentId: string | null;
  recurring: string;
  recurringTemplateId: string | null;
  crossStoreVariants: boolean;
  blockReason: string | null;
  adminNote: string | null;
  assigneeId: string | null;                    // legacy: primary R from owners
  primaryAssigneeId: string | null;              // explicit 主负责人 R (mirrors assigneeId)
  creatorId: string;
  createdAt: string;
  updatedAt: string;
  sortOrder: number;                            // drag-reorder order within group
  owners: Owner[];
  blockedBy: string[];
  variants: { store: string; quantity: number }[];
  updates: { at: string; by: string; text: string }[];
}

// ---------- error -----------------------------------------------------------

export class ApiError extends Error {
  constructor(public status: number, public payload: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> ?? {})
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  let payload: unknown;
  try { payload = await res.json(); } catch { payload = null; }
  if (!res.ok) {
    const msg = (payload && typeof payload === 'object' && 'error' in payload)
      ? String((payload as { error: unknown }).error)
      : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, payload, msg);
  }
  return payload as T;
}

// ---------- session ----------------------------------------------------------

export function loadSession(): { staff: Staff; token: string } | null {
  const token = getToken();
  const staffRaw = localStorage.getItem(STAFF_KEY);
  if (!token || !staffRaw) return null;
  try {
    return { token, staff: JSON.parse(staffRaw) as Staff };
  } catch {
    return null;
  }
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(STAFF_KEY);
}

/** Re-fetch the current staff record from the server and refresh the cached
 *  copy. Called on app load so capability changes (e.g. super_admin, new
 *  fields) propagate to already-logged-in users without a forced re-login. */
export async function fetchMe(): Promise<Staff> {
  const data = await request<{ staff: Staff }>('/auth/me');
  localStorage.setItem(STAFF_KEY, JSON.stringify(data.staff));
  return data.staff;
}

// ---------- public + auth ----------------------------------------------------

export async function listStaff(): Promise<Staff[]> {
  const data = await request<{ staff: Staff[] }>('/staff');
  return data.staff;
}

/** Log in. Accepts an email (preferred) or a staff_id; the backend decides
 * how to look up based on which field is present. Identifier is auto-trimmed
 * + lowercased if it looks like an email, so case mismatches don't break
 * sign-in. */
export async function login(identifier: string, password: string): Promise<{ token: string; staff: Staff }> {
  const trimmed = identifier.trim();
  const body = trimmed.includes('@')
    ? { email: trimmed.toLowerCase(), password }
    : { staff_id: trimmed, password };
  const data = await request<{ token: string; staff: Staff }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(body)
  });
  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(STAFF_KEY, JSON.stringify(data.staff));
  // Mirror token to IndexedDB so the service worker can fetch /notifications
  // when it receives an empty push. Best-effort.
  try {
    const mod = await import('./push');
    await mod.mirrorTokenToIdb(data.token);
  } catch { /* IDB unavailable, push will show generic fallback */ }
  return data;
}

export async function logout(): Promise<void> {
  try { await request('/auth/logout', { method: 'POST' }); } catch { /* ok */ }
  try {
    const mod = await import('./push');
    await mod.unsubscribeFromPush();
    await mod.mirrorTokenToIdb(null);
  } catch { /* ok */ }
  clearSession();
}

// ---------- identity: invite + set-password ---------------------------------

export interface InviteCheck {
  valid: boolean;
  reason?: 'not_found' | 'used' | 'expired' | 'not_invited';
  staff?: { id: string; name: string };
}

/** Validate an invite token without consuming it (set-password page load). */
export async function validateInvite(token: string): Promise<InviteCheck> {
  return request<InviteCheck>(`/auth/invite/${encodeURIComponent(token)}`);
}

/** Consume an invite token: set the password and auto-login. */
export async function setPassword(token: string, password: string): Promise<{ token: string; staff: Staff }> {
  const data = await request<{ token: string; staff: Staff }>('/auth/set-password', {
    method: 'POST',
    body: JSON.stringify({ token, password })
  });
  localStorage.setItem(TOKEN_KEY, data.token);
  localStorage.setItem(STAFF_KEY, JSON.stringify(data.staff));
  try {
    const mod = await import('./push');
    await mod.mirrorTokenToIdb(data.token);
  } catch { /* IDB unavailable */ }
  return data;
}

// ---------- identity: self-serve profile ------------------------------------

/** Update own name and/or email. Re-caches the staff record. */
export async function updateProfile(patch: { name?: string; email?: string | null }): Promise<Staff> {
  const data = await request<{ staff: Staff }>('/me', {
    method: 'PATCH',
    body: JSON.stringify(patch)
  });
  localStorage.setItem(STAFF_KEY, JSON.stringify(data.staff));
  return data.staff;
}

/** Change own password. Revokes other sessions server-side; current stays. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await request('/me/password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
  });
}

// ---------- identity: user management (/admin/users) ------------------------

export interface AdminUser {
  id: string;
  name: string;
  role: 'admin' | 'store_manager' | 'staff';
  stores: string[];
  title: string;
  email: string | null;
  dept: string | null;
  active: boolean;
  state: AccountState;
  canManageUsers: boolean;
  shift: 'morning' | 'evening' | null;   // 领班's led shift (role=staff + shift)
  csManager: boolean;                    // 客服管理员 (role=staff + cs_manager + dept=客服)
  createdAt: string;
  hasPendingInvite: boolean;
  locked: boolean;
  /** Most recent invite created — drives the "邀请已发送 X 天前" badge. */
  inviteSentAt: string | null;
  /** Most recent invite consumed (= user clicked the link and set a password). */
  activatedAt: string | null;
}

export interface UserDetail {
  user: AdminUser;
  open_r_tasks: { id: string; title: string; status: string }[];
  recurring_templates: { id: string; task_title: string }[];
}

export interface CreateUserInput {
  name: string;
  role: 'admin' | 'store_manager' | 'staff';
  stores?: string[];
  title?: string;
  email?: string;
  dept?: string | null;
  can_manage_users?: boolean;
  shift?: 'morning' | 'evening' | null;     // set when role=staff + 领班
  cs_manager?: boolean;                      // set when role=staff + 客服管理员 (super-admin only)
}

export interface UpdateUserInput {
  name?: string;
  role?: 'admin' | 'store_manager' | 'staff';
  stores?: string[];
  title?: string;
  email?: string | null;
  dept?: string | null;
  can_manage_users?: boolean;
  shift?: 'morning' | 'evening' | null;
  cs_manager?: boolean;
}

export async function listUsers(): Promise<AdminUser[]> {
  const data = await request<{ users: AdminUser[] }>('/admin/users');
  return data.users;
}

export async function getUser(id: string): Promise<UserDetail> {
  return request<UserDetail>(`/admin/users/${encodeURIComponent(id)}`);
}

export async function createUser(
  input: CreateUserInput
): Promise<{ user: AdminUser; has_email: boolean; invite_email_sent: boolean }> {
  return request<{ user: AdminUser; has_email: boolean; invite_email_sent: boolean }>(
    '/admin/users', { method: 'POST', body: JSON.stringify(input) }
  );
}

export async function updateUser(id: string, patch: UpdateUserInput): Promise<AdminUser> {
  const data = await request<{ user: AdminUser }>(`/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  });
  return data.user;
}

export async function deactivateUser(
  id: string,
  reassignments: Record<string, string>
): Promise<{ user: AdminUser; reassigned_tasks: number }> {
  return request<{ user: AdminUser; reassigned_tasks: number }>(
    `/admin/users/${encodeURIComponent(id)}/deactivate`,
    { method: 'POST', body: JSON.stringify({ reassignments }) }
  );
}

export async function reactivateUser(
  id: string
): Promise<{ user: AdminUser; has_email: boolean; invite_email_sent: boolean }> {
  return request<{ user: AdminUser; has_email: boolean; invite_email_sent: boolean }>(
    `/admin/users/${encodeURIComponent(id)}/reactivate`, { method: 'POST' }
  );
}

/** Send an invitation email to one user. Works for `invited` OR `active`
 * users — sending to an `active` user resets their password and forces them
 * to set a new one via the email link (`was_reset: true` in the response). */
export async function resendInvite(
  id: string
): Promise<{ invite_email_sent: boolean; was_reset?: boolean; email_error?: string }> {
  return request<{ invite_email_sent: boolean; was_reset?: boolean; email_error?: string }>(
    `/admin/users/${encodeURIComponent(id)}/resend-invite`, { method: 'POST' }
  );
}

// ---------- inventory: activity feed + stats --------------------------------
// (InventoryEvent type is declared further down with the other inventory types)

export async function loadInventoryEvents(
  limit = 25,
  window?: { fromUtc: string; toUtc: string }
): Promise<InventoryEvent[]> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (window) { qs.set('from_utc', window.fromUtc); qs.set('to_utc', window.toUtc); }
  const data = await request<{ events: InventoryEvent[] }>(`/inventory/events?${qs}`);
  return data.events;
}

export interface InventoryStatsBucket { produced: number; sold: number; wasted: number; }
export interface InventoryStats {
  today: InventoryStatsBucket;
  week:  InventoryStatsBucket;
  month: InventoryStatsBucket;
}

/** 今日 / 7天 / 30天 totals anchored to a selected ET day (default: today ET).
 *  All three windows are bounded by Toronto calendar-day midnights, so 今日 is
 *  the whole selected day (no post-midnight bleed) and 7天/30天 end on it. */
export async function loadInventoryStats(asOf?: string): Promise<InventoryStats> {
  const q = asOf ? `?as_of=${encodeURIComponent(asOf)}` : '';
  return request<InventoryStats>(`/inventory/stats${q}`);
}

export interface CellDelta { productId: string; location: Location; plus: number; minus: number; }

/** Per (product, location) +/− movement for an ET-day window — drives the small
 *  "+12 −12" second line in each grid cell. Server-aggregated by occurred_at so
 *  it's correct for any selected day (today or a 补录 day). */
// Edit presence: heartbeat that I'm editing `resource`, get back who else is.
export interface CoEditor { id: string; name: string; }
export async function postInventoryPresence(resource: string): Promise<CoEditor[]> {
  const data = await request<{ others: CoEditor[] }>('/inventory/presence', {
    method: 'POST', body: JSON.stringify({ resource })
  });
  return data.others ?? [];
}

export async function loadCellDeltas(fromUtc: string, toUtc: string): Promise<CellDelta[]> {
  const q = `?from_utc=${encodeURIComponent(fromUtc)}&to_utc=${encodeURIComponent(toUtc)}`;
  const data = await request<{ rows: CellDelta[] }>(`/inventory/cell-deltas${q}`);
  return data.rows || [];
}

/** Per-store bucket used inside StatsByProductRow.byLocation when the API
 *  was called with ?breakdown=location. */
export interface LocationBucket {
  produced: number;
  sold: number;
  wasted: number;
}

export interface StatsByProductRow {
  id: string;
  name: string;
  category: string | null;
  variant: string | null;
  size: string | null;
  shift: 'day' | 'night' | 'both';
  dailyTarget: number | null;
  active: boolean;
  produced: number;
  sold: number;
  wasted: number;
  /** wasted / produced — null when produced=0 (undefined ratio). */
  wasteRate: number | null;
  /** sold / produced — null when produced=0. */
  sellThrough: number | null;
  /** Only present when API was called with ?breakdown=location. */
  byLocation?: Record<Location, LocationBucket>;
}

export interface StatsByProductReport {
  from: string;
  to: string;
  rows: StatsByProductRow[];
}

export async function loadInventoryStatsByProduct(
  fromOrUtc: string, toOrUtc: string,
  opts: {
    includeInactive?: boolean;
    shift?: 'day' | 'night';
    location?: Location;
    breakdown?: 'location';   // include per-store byLocation buckets per row
    utc?: boolean;             // treat fromOrUtc/toOrUtc as ISO datetime instants
  } = {}
): Promise<StatsByProductReport> {
  const q = new URLSearchParams();
  if (opts.utc) {
    q.set('from_utc', fromOrUtc);
    q.set('to_utc', toOrUtc);
  } else {
    q.set('from', fromOrUtc);
    q.set('to', toOrUtc);
  }
  if (opts.includeInactive) q.set('include_inactive', '1');
  if (opts.shift) q.set('shift', opts.shift);
  if (opts.location) q.set('location', opts.location);
  if (opts.breakdown) q.set('breakdown', opts.breakdown);
  return request<StatsByProductReport>(`/inventory/stats/by-product?${q}`);
}

// ---------- daily targets (per shift, store-wide) ---------------------------

export interface DailyTarget {
  date: string;                 // YYYY-MM-DD
  shift: 'day' | 'night';
  targetUnits: number;
  targetText: string | null;
  setBy: string | null;
  updatedAt: string;
}

export async function loadDailyTargets(from: string, to: string): Promise<DailyTarget[]> {
  const q = new URLSearchParams({ from, to });
  const data = await request<{ targets: DailyTarget[] }>(`/daily-targets?${q}`);
  return data.targets;
}

export async function setDailyTarget(
  date: string, shift: 'day' | 'night', targetUnits: number, targetText: string | null = null
): Promise<void> {
  await request('/daily-targets', {
    method: 'PUT',
    body: JSON.stringify({ date, shift, target_units: targetUnits, target_text: targetText })
  });
}

export async function clearDailyTarget(date: string, shift: 'day' | 'night'): Promise<void> {
  await request(`/daily-targets/${encodeURIComponent(date)}/${encodeURIComponent(shift)}`, {
    method: 'DELETE'
  });
}

// ---------- pivot for the Excel-template export -----------------------------

export interface PivotCell { produced: number; sold: number; wasted: number; }

export interface PivotProductRow {
  id: string;
  name: string;
  category: string | null;
  variant: string | null;
  size: string | null;
  shift: 'day' | 'night' | 'both';
  dailyTarget: number | null;
  perDay: Record<string, PivotCell>;
  totals: PivotCell;
  wasteRate: number | null;
  sellThrough: number | null;
}

export interface PivotReport {
  from: string;
  to: string;
  shift: 'day' | 'night' | null;
  location: Location | null;
  dates: string[];
  products: PivotProductRow[];
  targets: Record<string, { targetUnits: number; targetText: string | null }>;
}

// ---------- admin-only event deletion ---------------------------------------

/** Delete one ledger event by id. Admin only. Transfer events delete both
 *  halves atomically to keep balances consistent. */
export async function deleteInventoryEvent(id: number): Promise<{ deleted: number }> {
  return request(`/inventory/events/${id}`, { method: 'DELETE' });
}

/** Wipe ALL ledger events for one SKU. Admin only. Used by Regina to clean
 *  up testing data so the SKU's numbers reset to zero. */
export async function wipeProductEvents(productId: string): Promise<{ deleted: number }> {
  return request(`/products/${encodeURIComponent(productId)}/events`, { method: 'DELETE' });
}

// ---------- 重设该日 — reset one SKU's data for one ET calendar day (T4) -----

/** Per-location / total cleared amounts recorded on a reset marker. produced/
 *  sold/wasted are positive magnitudes; transferred keeps its sign. */
export interface ResetCleared {
  produced: number;
  sold: number;
  wasted: number;
  transferred: number;
}

export interface ResetClearedReport {
  byLocation: Partial<Record<Location, ResetCleared>>;
  totals: ResetCleared;
}

export interface ResetDayResult {
  ok: boolean;
  deleted: number;
  reset_date: string;
  cleared: ResetClearedReport;
}

/** Reset one SKU's data for one ET calendar day. Admin + 店长 only. Deletes
 *  that day's ledger rows for the SKU and records a permanent marker. The
 *  balance reverts to the prior carryover (not zero). The server derives the
 *  ET-day window from `resetDate` — the client does not send bounds. */
export async function resetProductDay(productId: string, resetDate: string): Promise<ResetDayResult> {
  return request(`/products/${encodeURIComponent(productId)}/reset-day`, {
    method: 'POST',
    body: JSON.stringify({ reset_date: resetDate })
  });
}

export interface ResetStockResult {
  ok: boolean;
  adjusted: { location: Location; delta: number }[];
  count: number;
}

/** Zero the SKU's current stock at every location by appending compensating
 *  `adjusted` events (keeps full ledger history). Admin + 店长 only. Distinct
 *  from resetProductDay, which only removes a single day's deltas. */
export async function resetProductStock(productId: string): Promise<ResetStockResult> {
  return request(`/products/${encodeURIComponent(productId)}/reset-stock`, { method: 'POST' });
}

export interface InventoryReset {
  id: number;
  productId: string;
  resetDate: string;
  actorId: string | null;
  actorName: string | null;
  cleared: ResetClearedReport | null;
  deletedCount: number;
  createdAt: string;
}

/** Recent reset markers for one SKU — merged into the slide-over feed. */
export async function listProductResets(productId: string, limit = 25): Promise<InventoryReset[]> {
  const data = await request<{ resets: InventoryReset[] }>(
    `/products/${encodeURIComponent(productId)}/resets?limit=${limit}`
  );
  return data.resets;
}

export async function loadInventoryPivot(
  fromOrUtc: string, toOrUtc: string,
  opts: { shift?: 'day' | 'night'; location?: Location; utc?: boolean } = {}
): Promise<PivotReport> {
  const q = new URLSearchParams();
  if (opts.utc) {
    q.set('from_utc', fromOrUtc);
    q.set('to_utc', toOrUtc);
  } else {
    q.set('from', fromOrUtc);
    q.set('to', toOrUtc);
  }
  if (opts.shift) q.set('shift', opts.shift);
  if (opts.location) q.set('location', opts.location);
  return request<PivotReport>(`/inventory/stats/pivot?${q}`);
}

export interface FrontdeskReport {
  from: string;
  to: string;
  dates: string[];
  stores: string[];
  products: { id: string; name: string; category: string | null; variant: string | null; size: string | null }[];
  sold: Record<string, Record<string, Record<string, number>>>;   // [store][date][productId] = qty
  actors: Record<string, Record<string, string[]>>;               // [store][date] = logger names
}

// 前台当值汇报: per (date × store × product) sold counts. Revenue waits on prices.
export async function loadFrontdeskReport(fromUtc: string, toUtc: string): Promise<FrontdeskReport> {
  const q = new URLSearchParams();
  q.set('from_utc', fromUtc);
  q.set('to_utc', toUtc);
  return request<FrontdeskReport>(`/inventory/stats/frontdesk?${q}`);
}

// ---------- pre-orders + 后厨 sheet (Phase 2) --------------------------------

export interface OrderRow {
  id: string; pickupDate: string; pickupTime: string | null; store: string;
  productId: string; productName: string; category: string | null; size: string | null;
  qty: number; customerName: string | null; customerPhone: string | null;
  channel: string | null; payStatus: string; depositAmt: number | null; note: string | null;
  status: string; fulfilledByName: string | null; createdByName: string | null; createdAt: string;
}
export interface KitchenCell { ordered: number; onHand: number; total: number; bake: number }
export interface KitchenRow {
  productId: string; name: string; category: string | null; variant: string | null; size: string | null;
  perStore: Record<string, KitchenCell>; total: KitchenCell;
}
export interface KitchenSheet {
  date: string; prevClose: string; stores: string[]; rows: KitchenRow[];
  confirmed: Record<string, { confirmedByName: string | null; confirmedAt: string }>; allConfirmed: boolean;
}

export function listOrders(date: string, store?: string): Promise<{ orders: OrderRow[] }> {
  const q = new URLSearchParams({ date }); if (store) q.set('store', store);
  return request(`/orders?${q}`);
}
export function createOrder(body: Record<string, unknown>): Promise<{ ok: boolean; id: string }> {
  return request('/orders', { method: 'POST', body: JSON.stringify(body) });
}
export function updateOrder(id: string, body: Record<string, unknown>): Promise<{ ok: boolean }> {
  return request(`/orders/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) });
}
export function deleteOrder(id: string): Promise<{ ok: boolean }> {
  return request(`/orders/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export function fulfillOrder(id: string): Promise<{ ok: boolean }> {
  return request(`/orders/${encodeURIComponent(id)}/fulfill`, { method: 'POST' });
}
export function unfulfillOrder(id: string): Promise<{ ok: boolean }> {
  return request(`/orders/${encodeURIComponent(id)}/unfulfill`, { method: 'POST' });
}
export function loadKitchenSheet(date: string): Promise<KitchenSheet> {
  return request(`/orders/kitchen-sheet?date=${date}`);
}
export function loadOrderDays(date: string): Promise<{ date: string; stores: string[]; confirmed: Record<string, { confirmedByName: string | null; confirmedAt: string }>; allConfirmed: boolean }> {
  return request(`/order-days?date=${date}`);
}
export function confirmOrderDay(date: string, store?: string): Promise<{ ok: boolean }> {
  return request('/order-days/confirm', { method: 'POST', body: JSON.stringify({ date, store }) });
}
export function unconfirmOrderDay(date: string, store?: string): Promise<{ ok: boolean }> {
  return request('/order-days/unconfirm', { method: 'POST', body: JSON.stringify({ date, store }) });
}
export function loadPendingConfirm(): Promise<{ date: string; unconfirmedStores: string[]; pending: boolean }> {
  return request('/orders/pending-confirm');
}

// ---------- 备货录入 prep + 店长 certify (Phase 3) ----------------------------
// The nightly plan has two lanes — 订单 (orders, above) and 现货 (stock_plan,
// here). 录入完成 materializes (orders+stock)→ledger once per (date, store).
// 店长 certify reconciles a past day's actual so plan-vs-actual powers 备货准确率.

export interface PrepPlanRow { id: number; store: string; product_id: string; planned_qty: number; }
export interface PrepLane { ordersDone: boolean; stockDone: boolean; materialized: boolean; }
export interface PrepDay { date: string; plan: PrepPlanRow[]; lanes: Record<string, PrepLane>; }

export function loadPrep(date: string): Promise<PrepDay> {
  return request(`/prep?date=${date}`);
}
export function upsertPrep(body: { plan_date: string; store: string; product_id: string; planned_qty: number }): Promise<{ ok: boolean }> {
  return request('/prep', { method: 'POST', body: JSON.stringify(body) });
}
export function deletePrep(id: number): Promise<{ ok: boolean }> {
  return request(`/prep/${id}`, { method: 'DELETE' });
}
export function setPrepLaneDone(body: { date: string; store: string; lane: 'orders' | 'stock'; done?: boolean }): Promise<{ ok: boolean }> {
  return request('/prep/lane-done', { method: 'POST', body: JSON.stringify(body) });
}
export function completePrep(date: string): Promise<{ ok: boolean; products: number }> {
  return request('/prep/complete', { method: 'POST', body: JSON.stringify({ date }) });
}
export function uncompletePrep(date: string, store: string): Promise<{ ok: boolean }> {
  return request('/prep/uncomplete', { method: 'POST', body: JSON.stringify({ date, store }) });
}

export interface CertifyPending { pending: { date: string; store: string }[]; oldestByStore: Record<string, string>; count: number; }
export function loadCertifyPending(): Promise<CertifyPending> {
  return request('/certify/pending');
}
export interface CertifyDay {
  date: string; stores: string[];
  certified: Record<string, { onHandClose: number | null; certifiedByName: string | null; certifiedAt: string }>;
  allCertified: boolean;
}
export function loadCertify(date: string): Promise<CertifyDay> {
  return request(`/certify?date=${date}`);
}
export function certifyStore(date: string, store: string): Promise<{ ok: boolean; onHandClose: number | null }> {
  return request('/certify', { method: 'POST', body: JSON.stringify({ date, store }) });
}

// ---------- 备货分析 analytics (Phase 3 · T9/T10/T11/T12) ---------------------
// 现货 = certified actual WALK-IN sold; 订单 = order pickups; 准确率 = planned
// stock vs actual walk-in (certified days only). See worker/src/routes/analytics.ts.

export interface PrepReportRow {
  productId: string; name: string; category: string | null; variant: string | null; size: string | null;
  orders: number; stock: number; stockCertified: number; total: number;
  planned: number; accuracy: number | null; certifiedDays: number; hasUncertified: boolean;
}
export function loadPrepReport(from: string, to: string, store?: string): Promise<{ from: string; to: string; store: string | null; rows: PrepReportRow[] }> {
  const q = new URLSearchParams({ from, to }); if (store) q.set('store', store);
  return request(`/prep/report?${q}`);
}

/** Bulk: send invitation emails to every active-or-invited staff with an
 * email. Used for the "邀请全员" onboarding button. */
export interface InviteAllResult {
  total: number;
  sent: number;
  failed: number;
  skipped: number;
  results: { id: string; ok: boolean; sent?: boolean; was_reset?: boolean; error?: string }[];
}
export async function inviteAll(): Promise<InviteAllResult> {
  return request<InviteAllResult>('/admin/users/invite-all', { method: 'POST' });
}

/** Upload (or replace) the current user's profile picture. Returns the
 * refreshed staff record (with the new `profilePicKey`) so the caller can
 * update local cache + force avatars to re-fetch with a new cache key. */
export async function uploadProfilePic(file: File): Promise<Staff> {
  const token = getToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}/me/profile-pic`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form
  });
  let payload: unknown;
  try { payload = await res.json(); } catch { payload = null; }
  if (!res.ok) {
    const msg = (payload && typeof payload === 'object' && 'error' in payload)
      ? String((payload as { error: unknown }).error)
      : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, payload, msg);
  }
  const staff = (payload as { staff: Staff }).staff;
  localStorage.setItem(STAFF_KEY, JSON.stringify(staff));
  return staff;
}

export async function clearLockout(id: string): Promise<void> {
  await request(`/admin/users/${encodeURIComponent(id)}/clear-lockout`, { method: 'POST' });
}

// ---------- tasks ------------------------------------------------------------

/** Bulk-reorder main tasks. Frontend sends the new ordered id list after a
 *  drag-and-drop; backend assigns sort_order = 1..N in that order. */
export async function reorderTasks(orderedIds: string[]): Promise<{ count: number }> {
  return request('/tasks/order', {
    method: 'PUT',
    body: JSON.stringify({ ordered_ids: orderedIds })
  });
}

export async function loadTasks(): Promise<Task[]> {
  const data = await request<{ tasks: Task[] }>('/tasks');
  return data.tasks;
}

// ----- real-time task sync (TaskHub Durable Object) -------------------------
// Mint a short-lived single-use WS ticket (authed), then build the wss URL the
// browser connects to. The bearer token never rides in the URL — only the ticket.
export function fetchWsTicket(): Promise<{ ticket: string }> {
  return request('/tasks/ws-ticket', { method: 'POST' });
}
export function taskWsUrl(ticket: string): string {
  const base = API_BASE.replace(/^http/i, 'ws');   // http→ws, https→wss
  return `${base}/tasks/ws?ticket=${encodeURIComponent(ticket)}`;
}

export async function getTask(id: string): Promise<Task> {
  const data = await request<{ task: Task }>(`/tasks/${encodeURIComponent(id)}`);
  return data.task;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  priority?: 'P0' | 'P1' | 'P2';
  category?: string;
  group_id?: string | null;
  due_date?: string | null;
  type?: 'general' | 'training';
  parent_id?: string | null;
  recurring?: string;
  cross_store_variants?: boolean;
  owners: Owner[];
}

export async function createTask(input: CreateTaskInput): Promise<Task> {
  const data = await request<{ task: Task }>('/tasks', {
    method: 'POST',
    body: JSON.stringify(input)
  });
  return data.task;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string;
  status?: Task['status'];
  priority?: Task['priority'];
  due_date?: string | null;
  start_date?: string | null;
  admin_note?: string;
  block_reason?: string;
  group_id?: string | null;
  parent_id?: string | null;
}

export async function updateTask(id: string, patch: UpdateTaskInput): Promise<Task> {
  const data = await request<{ task: Task }>(`/tasks/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  });
  return data.task;
}

export async function deleteTask(id: string): Promise<void> {
  await request(`/tasks/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---------- trash bin (soft delete + restore) -------------------------------

export async function listTrash(): Promise<any[]> {
  const data = await request<{ trash: any[] }>('/tasks/trash');
  return data.trash;
}
export async function restoreTask(id: string): Promise<void> {
  await request(`/tasks/${encodeURIComponent(id)}/restore`, { method: 'POST' });
}
export async function purgeTask(id: string): Promise<void> {
  await request(`/tasks/${encodeURIComponent(id)}/permanent`, { method: 'DELETE' });
}

export async function addTaskUpdate(taskId: string, text: string): Promise<void> {
  await request(`/tasks/${encodeURIComponent(taskId)}/updates`, {
    method: 'POST',
    body: JSON.stringify({ text })
  });
}

export async function addDependency(taskId: string, blockedById: string): Promise<void> {
  await request(`/tasks/${encodeURIComponent(taskId)}/dependency`, {
    method: 'POST',
    body: JSON.stringify({ blocked_by_id: blockedById })
  });
}

export async function removeDependency(taskId: string, blockedById: string): Promise<void> {
  await request(
    `/tasks/${encodeURIComponent(taskId)}/dependency/${encodeURIComponent(blockedById)}`,
    { method: 'DELETE' }
  );
}

// ---------- notifications ---------------------------------------------------

export interface Notification {
  id: number;
  kind: 'task_assigned' | 'task_comment' | 'task_split' | 'task_completed' | 'dependency_unblocked' | 'task_mention';
  taskId: string | null;
  actorId: string | null;
  message: string;
  readAt: string | null;
  createdAt: string;
}

export async function loadNotifications(): Promise<{ notifications: Notification[]; unread: number }> {
  return request<{ notifications: Notification[]; unread: number }>('/notifications');
}

export async function markNotificationRead(id: number): Promise<void> {
  await request(`/notifications/${id}/read`, { method: 'POST' });
}

export async function markAllNotificationsRead(): Promise<void> {
  await request('/notifications/read-all', { method: 'POST' });
}

// ---------- observability ---------------------------------------------------

export interface ClientEvent {
  kind: 'app_open' | 'page_view' | 'frontend_error' | 'notification_click';
  path?: string | null;
  device?: 'mobile' | 'desktop' | null;
  pwa?: 0 | 1;
  session_id?: string | null;
  error_msg?: string | null;
  error_stack?: string | null;
  metadata?: unknown;
}

export async function postEvents(events: ClientEvent[]): Promise<void> {
  await request('/events', { method: 'POST', body: JSON.stringify({ events }) });
}

export interface WeeklySelf {
  tasks_completed_7d: number;
  comments_7d: number;
  tasks_created_7d: number;
}

export async function loadMyWeekly(): Promise<WeeklySelf> {
  return request<WeeklySelf>('/me/weekly');
}

export interface AdminOps {
  dau_heatmap: { staff_id: string; name: string; days: number[] }[];
  days: string[];
  per_staff: {
    staff_id: string; name: string; title: string;
    last_login: string | null;
    tasks_created_7d: number; tasks_completed_7d: number; comments_7d: number;
    last_event_at: string | null;
    status: '活跃' | '沉睡' | '失联';
  }[];
  notifications: {
    sent_7d: number; read_7d: number;
    read_rate: number | null;
    median_minutes_to_read: number | null;
  };
  device_split: { mobile: number; desktop: number; pwa: number };
  errors_24h: {
    count: number;
    recent: { id: number; actor_id: string; at: string; error_msg: string | null; path: string | null }[];
  };
  telemetry_health: { total: number; last_hour: number };
}

export async function loadAdminOps(): Promise<AdminOps> {
  return request<AdminOps>('/admin/ops');
}

// Monthly completion report
export interface MonthlyCell {
  month: string;          // "YYYY-MM"
  completed: number;       // tasks completed in this month
  due: number;             // tasks due in this month
  on_time: number;         // completed and finished by due-date end
}

export interface MonthlyStaff {
  staff_id: string;
  name: string;
  title: string;
  months: MonthlyCell[];
}

export interface MonthlyReport {
  months: string[];
  per_staff: MonthlyStaff[];
  company: MonthlyCell[];
  generated_at: string;
}

export async function loadMonthlyReport(months: number = 6): Promise<MonthlyReport> {
  return request<MonthlyReport>(`/admin/monthly?months=${months}`);
}

// ---------- file attachments ------------------------------------------------

export interface Attachment {
  id: string;
  taskId: string;
  filename: string;
  sizeBytes: number;
  mime: string;
  uploadedBy: string;
  uploadedAt: string;
}

export async function listAttachments(taskId: string): Promise<Attachment[]> {
  const data = await request<{ attachments: Attachment[] }>(
    `/tasks/${encodeURIComponent(taskId)}/attachments`
  );
  return data.attachments;
}

export async function uploadAttachment(taskId: string, file: File): Promise<Attachment> {
  const token = getToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}/tasks/${encodeURIComponent(taskId)}/attachments`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form
    // NOTE: don't set Content-Type — fetch sets it with the multipart boundary.
  });
  let payload: unknown;
  try { payload = await res.json(); } catch { payload = null; }
  if (!res.ok) {
    const msg = (payload && typeof payload === 'object' && 'error' in payload)
      ? String((payload as { error: unknown }).error)
      : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, payload, msg);
  }
  return (payload as { attachment: Attachment }).attachment;
}

export async function deleteAttachment(id: string): Promise<void> {
  await request(`/attachments/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Fetch an attachment as a blob: URL for use in <img src>. The download
// endpoint sets Content-Disposition: attachment, but when we fetch it as a
// blob via JS that header doesn't matter — the blob URL is treated as
// whatever MIME the response declared. Caller must revokeObjectURL when done.
export async function fetchAttachmentBlobUrl(id: string): Promise<string> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/attachments/${encodeURIComponent(id)}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { const p = await res.json(); if (p && typeof p === 'object' && 'error' in p) msg = String((p as { error: unknown }).error); } catch { /* ok */ }
    throw new ApiError(res.status, null, msg);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Fetch an attachment as a raw Blob (authed) — for client-side rendering of
// Office docs (docx-preview / exceljs) where we need the bytes, not a URL.
export async function fetchAttachmentBlob(id: string): Promise<Blob> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/attachments/${encodeURIComponent(id)}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { const p = await res.json(); if (p && typeof p === 'object' && 'error' in p) msg = String((p as { error: unknown }).error); } catch { /* ok */ }
    throw new ApiError(res.status, null, msg);
  }
  return res.blob();
}

// Build a download URL that includes the auth token as a query param fallback
// is NOT used — we just use anchor + fetch + blob trick to authorize the GET.
// But for the simple case (modern browsers), open in same-tab triggers the
// Content-Disposition download. We use a helper that does fetch+blob.
// ---------- groups (taxonomy) -----------------------------------------------

export interface Group {
  id: string;
  name: string;
  parentId: string | null;
  displayOrder: number;
  description: string | null;
  icon: string | null;
  archived: 0 | 1;
}

export async function listGroups(): Promise<Group[]> {
  const data = await request<{ groups: Group[] }>('/groups');
  return data.groups;
}

export async function createGroup(input: {
  id: string; name: string; parent_id?: string | null;
  display_order?: number; description?: string; icon?: string;
}): Promise<void> {
  await request('/groups', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateGroup(id: string, patch: Partial<{
  name: string; parent_id: string | null; display_order: number;
  description: string; icon: string; archived: 0 | 1;
}>): Promise<void> {
  await request(`/groups/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch)
  });
}

export async function archiveGroup(id: string): Promise<void> {
  await request(`/groups/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---------- task owners replace ---------------------------------------------

export async function replaceOwners(
  taskId: string,
  owners: { staff_id: string; role: 'R' | 'A' | 'C' | 'I'; is_primary?: boolean }[]
): Promise<Task> {
  const data = await request<{ task: Task }>(
    `/tasks/${encodeURIComponent(taskId)}/owners`,
    { method: 'PUT', body: JSON.stringify({ owners }) }
  );
  return data.task;
}

// ---------- project templates -----------------------------------------------

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string | null;
  groupId: string | null;
  archived: 0 | 1;
  createdBy: string;
  createdAt: string;
  subtasks: {
    ord: number;
    title: string;
    description: string | null;
    priority: 'P0' | 'P1' | 'P2';
    offsetDays: number;
    owners: { staffId: string; role: 'R' | 'A' | 'C' | 'I' }[];
  }[];
}

export async function listProjectTemplates(): Promise<ProjectTemplate[]> {
  const data = await request<{ templates: ProjectTemplate[] }>('/project-templates');
  return data.templates;
}

export interface TemplateSubtaskInput {
  ord: number;
  title: string;
  description?: string | null;
  priority?: 'P0' | 'P1' | 'P2';
  offset_days: number;
  owners?: { staffId: string; role: 'R' | 'A' | 'C' | 'I' }[];
}

export interface TemplateInput {
  id: string;
  name: string;
  description?: string | null;
  group_id?: string | null;
  subtasks: TemplateSubtaskInput[];
}

export async function createTemplate(input: TemplateInput): Promise<void> {
  await request('/project-templates', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateTemplate(id: string, input: Omit<TemplateInput, 'id'>): Promise<void> {
  await request(`/project-templates/${encodeURIComponent(id)}`, {
    method: 'PUT', body: JSON.stringify(input)
  });
}

export async function archiveTemplate(id: string): Promise<void> {
  await request(`/project-templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function instantiateTemplate(
  templateId: string,
  input: {
    title: string;
    anchor_date: string;
    description?: string;
    priority?: 'P0' | 'P1' | 'P2';
    parent_id?: string | null;
  }
): Promise<Task> {
  const data = await request<{ task: Task }>(
    `/project-templates/${encodeURIComponent(templateId)}/instantiate`,
    { method: 'POST', body: JSON.stringify(input) }
  );
  return data.task;
}

// ---------- inventory (Phase 2a) --------------------------------------------

// Location vocabulary lives in lib/locations.ts (single source post CK split).
// Import locally (so `Location` here is the union, not the DOM global) AND
// re-export for the rest of the app.
import type { Location } from './locations';
export type { Location };
export { LOCATIONS, STOREFRONTS, KITCHENS, locationLabel } from './locations';
export type EventKind = 'produced' | 'received' | 'sold' | 'wasted' | 'transferred' | 'adjusted';
// All reason values the backend stores (legacy + current). The entry UI only
// OFFERS WASTE_REASON_OPTIONS below; the legacy three survive for old rows.
export const WASTE_REASONS = ['expired', 'damaged', 'overproduction', 'display', 'donated', 'staff_taken', 'other'] as const;
export type WasteReason = typeof WASTE_REASONS[number];
// Reasons offered for NEW 报废 entries (Regina, 2026-05-29): 损坏 / 捐献 / 店员取走 / 其他.
// 其他 (other) requires a free-text 备注 explaining the reason.
export const WASTE_REASON_OPTIONS = ['damaged', 'donated', 'staff_taken', 'other'] as const;

export type Shift = 'day' | 'night' | 'both';

export interface Product {
  id: string;
  name: string;
  category: string | null;
  variant: string | null;
  size: string | null;
  shift: Shift;
  dailyTarget: number | null;
  active: boolean;
  createdAt: string;
}

export interface InventoryEvent {
  id: number;
  kind: EventKind;
  productId: string;
  productName: string | null;         // joined from products (null if hard-deleted)
  location: Location;
  qty: number;                        // signed (positive in / negative out)
  transferGroupId: string | null;
  actorId: string | null;             // null only if staff row was somehow lost
  actorName: string | null;           // joined from staff (display name for the feed)
  reason: string | null;
  note: string | null;
  takenBy: string | null;             // staff id who took the stock (staff_taken waste)
  takenByCustomId?: string | null;    // custom taker id (non-staff person)
  takenByName?: string | null;        // resolved name (staff OR custom taker, feed display)
  createdAt: string;
}

export interface GridRow {
  id: string;
  name: string;
  category: string | null;
  variant: string | null;
  size: string | null;
  shift: Shift;
  balances: Record<Location, number>;
}

// ----- products

export async function listProducts(
  opts: { includeInactive?: boolean; shift?: Shift } = {}
): Promise<Product[]> {
  const q = new URLSearchParams();
  if (opts.includeInactive) q.set('all', '1');
  if (opts.shift === 'day' || opts.shift === 'night') q.set('shift', opts.shift);
  const path = '/products' + (q.toString() ? `?${q}` : '');
  const data = await request<{ products: Product[] }>(path);
  return data.products;
}

export async function createProduct(input: {
  name: string;
  category?: string | null;
  variant?: string | null;
  size?: string | null;
  shift?: Shift;
  daily_target?: number | null;
}): Promise<Product> {
  const data = await request<{ product: Product }>('/products', {
    method: 'POST',
    body: JSON.stringify(input)
  });
  return data.product;
}

export async function updateProduct(
  id: string,
  patch: {
    name?: string;
    category?: string | null;
    variant?: string | null;
    size?: string | null;
    shift?: Shift;
    daily_target?: number | null;
    active?: boolean;
  }
): Promise<void> {
  await request(`/products/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(patch)
  });
}

export async function deleteProduct(id: string): Promise<{ soft_deleted: boolean }> {
  return request<{ ok: boolean; soft_deleted: boolean }>(
    `/products/${encodeURIComponent(id)}`, { method: 'DELETE' }
  );
}

// ----- grid

export async function loadInventoryGrid(opts: { shift?: Shift; asOfUtc?: string } = {}): Promise<GridRow[]> {
  const q = new URLSearchParams();
  if (opts.shift === 'day' || opts.shift === 'night') q.set('shift', opts.shift);
  if (opts.asOfUtc) q.set('as_of_utc', opts.asOfUtc);   // point-in-time balances
  const path = '/inventory/grid' + (q.toString() ? `?${q}` : '');
  const data = await request<{ products: GridRow[] }>(path);
  return data.products;
}

// ----- events

export interface LogEventInput {
  kind: 'produced' | 'received' | 'sold' | 'wasted' | 'adjusted';
  product_id: string;
  location: Location;
  qty: number;                        // positive for produced/received/sold/wasted; signed for adjusted
  reason?: string;
  note?: string;
  // 店员取走 — exactly one of these three on a staff_taken waste:
  taken_by?: string;                  // staff id (real employee)
  taken_by_custom_id?: string;        // existing custom taker id (recalled)
  new_taker_name?: string;            // free-text name → find-or-create
  occurred_at?: string;               // UTC ISO; backfill only (elevated)
}

export async function logInventoryEvent(input: LogEventInput): Promise<InventoryEvent> {
  const data = await request<{ event: InventoryEvent }>('/inventory/events', {
    method: 'POST', body: JSON.stringify(input)
  });
  return data.event;
}

// 改写 / override — set a (product, location) balance to an absolute target.
// Server computes the delta atomically + appends an audited `adjusted` event.
// 店长+ only (server-enforced). noop=true when target already equals current.
export async function adjustInventory(input: {
  product_id: string; location: Location; target: number; reason?: string; note: string;
}): Promise<{ ok: boolean; noop?: boolean; balance: number }> {
  return request('/inventory/adjust', { method: 'POST', body: JSON.stringify(input) });
}

// 撤销生产 / reverse production — for a production that did not actually happen,
// discovered after the 5-min 撤回 window. Appends a NEGATIVE `produced` event so
// the 生产 tally (and waste-rate) nets down, which 改写 cannot do. 店长+ only.
export async function reverseProduction(input: {
  product_id: string; location: Location; qty: number; note: string;
}): Promise<{ ok: boolean; balance: number }> {
  return request('/inventory/produce-reversal', { method: 'POST', body: JSON.stringify(input) });
}

// 撤回 / undo — remove a just-logged event (within 5 min, by the actor or 店长+).
export async function undoInventoryEvent(id: number): Promise<{ ok: boolean; deleted: number }> {
  return request(`/inventory/events/${id}/undo`, { method: 'POST' });
}

// ----- 店员取走 custom takers (non-staff names + admin tool)

export interface CustomTaker { id: string; name: string; }

export interface AdminCustomTaker {
  id: string; name: string; active: boolean; mergedInto: string | null;
  usageCount: number; createdAt: string; lastUsedAt: string;
}

// Recall list for the 店员取走 dropdown (active, not-merged, ≤20 most-recent).
export async function listCustomTakers(): Promise<CustomTaker[]> {
  const data = await request<{ takers: CustomTaker[] }>('/inventory/custom-takers');
  return data.takers || [];
}

// Admin (店长以上): full list with usage counts.
export async function listAdminCustomTakers(): Promise<AdminCustomTaker[]> {
  const data = await request<{ takers: AdminCustomTaker[] }>('/admin/custom-takers');
  return data.takers || [];
}

// Admin: rename and/or toggle active. Pass { name } and/or { active }.
export async function updateCustomTaker(
  id: string, patch: { name?: string; active?: boolean }
): Promise<{ taker: { id: string; name: string; active: boolean; mergedInto: string | null } }> {
  return request(`/admin/custom-takers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

// Admin: alias-chain merge `id` into `into` (events untouched).
export async function mergeCustomTaker(
  id: string, into: string
): Promise<{ ok: boolean; id: string; mergedInto: string }> {
  return request(`/admin/custom-takers/${id}/merge`, { method: 'POST', body: JSON.stringify({ into }) });
}

// Per-user product order for 当日现货 — an ordered list of pinned product ids.
// Scoped to the caller; pinned products float to the top of the grid in this
// order, unpinned ones show below. Synced server-side (follows the account).
export async function getMyInventoryOrder(): Promise<string[]> {
  const data = await request<{ productIds: string[] }>('/inventory/my-order');
  return data.productIds || [];
}
export async function saveMyInventoryOrder(productIds: string[]): Promise<string[]> {
  const data = await request<{ ok: boolean; productIds: string[] }>('/inventory/my-order', {
    method: 'PUT', body: JSON.stringify({ productIds })
  });
  return data.productIds || [];
}

// ---- 一键清零 (data reset, testing-stage) -----------------------------------
export interface ResetBatch {
  id: string;
  actorName: string | null;
  scopes: string[];
  dates: string[] | null;
  scopeLabel: string;
  counts: Record<string, number>;
  total: number;
  createdAt: string;
  restoredAt: string | null;
}
export async function clearData(input: {
  password: string;
  scopes: string[];
  dates?: string[];
  windows?: { from: string; to: string }[];
  locations?: string[];   // store keys (RH/DT/NY/CK); omit/empty = all locations (inventory only)
}): Promise<{ ok: boolean; batchId: string; counts: Record<string, number>; total: number }> {
  return request('/admin/data-reset/clear', { method: 'POST', body: JSON.stringify(input) });
}
export async function listResetBatches(): Promise<ResetBatch[]> {
  const data = await request<{ batches: ResetBatch[] }>('/admin/data-reset/batches');
  return data.batches || [];
}
export async function restoreResetBatch(id: string): Promise<{ ok: boolean; restored: number }> {
  return request(`/admin/data-reset/batches/${id}/restore`, { method: 'POST' });
}

// ---- 每周备份 (weekly inventory backup) -------------------------------------
export interface WeeklyBackup {
  id: string;
  weekStart: string;
  weekEnd: string;
  kind: 'auto' | 'manual';
  counts: Record<string, number>;
  total: number;
  createdAt: string;
}
export async function listWeeklyBackups(): Promise<WeeklyBackup[]> {
  const data = await request<{ backups: WeeklyBackup[] }>('/admin/weekly-backups');
  return data.backups || [];
}
export async function getWeeklyBackup(id: string): Promise<WeeklyBackup & { payload: { inventory: Record<string, unknown>[] } }> {
  return request(`/admin/weekly-backups/${id}`);
}
export async function runWeeklyBackup(): Promise<{ ok: boolean; id: string; weekStart: string; weekEnd: string; count: number }> {
  return request('/admin/weekly-backups/run', { method: 'POST' });
}

export interface TransferInput {
  product_id: string;
  from_location: Location;
  to_location: Location;
  qty: number;
  note?: string;
  occurred_at?: string;               // UTC ISO; backfill only (elevated)
}

export async function transferInventory(input: TransferInput): Promise<{ transferGroupId: string; events: InventoryEvent[] }> {
  const data = await request<{ transfer_group_id: string; events: InventoryEvent[] }>(
    '/inventory/transfer', { method: 'POST', body: JSON.stringify(input) }
  );
  return { transferGroupId: data.transfer_group_id, events: data.events };
}

export async function listInventoryEvents(opts: {
  product_id?: string; location?: Location; limit?: number;
} = {}): Promise<InventoryEvent[]> {
  const qs = new URLSearchParams();
  if (opts.product_id) qs.set('product_id', opts.product_id);
  if (opts.location) qs.set('location', opts.location);
  if (opts.limit) qs.set('limit', String(opts.limit));
  const path = '/inventory/events' + (qs.toString() ? `?${qs}` : '');
  const data = await request<{ events: InventoryEvent[] }>(path);
  return data.events;
}

// ---------- inventory · low-stock thresholds (Phase 2b) ---------------------

export interface LowStockThreshold {
  productId: string;
  location: Location;
  threshold: number;
  lastAlertedAt: string | null;
  updatedAt: string;
}

export async function listThresholds(): Promise<LowStockThreshold[]> {
  const data = await request<{ thresholds: LowStockThreshold[] }>('/low-stock-thresholds');
  return data.thresholds;
}

export async function setThreshold(
  productId: string, location: Location, threshold: number
): Promise<void> {
  await request('/low-stock-thresholds', {
    method: 'POST',
    body: JSON.stringify({ product_id: productId, location, threshold })
  });
}

export async function clearThreshold(productId: string, location: Location): Promise<void> {
  await request(
    `/low-stock-thresholds/${encodeURIComponent(productId)}/${encodeURIComponent(location)}`,
    { method: 'DELETE' }
  );
}

// ---------- inventory · waste breakdown (Phase 2b) --------------------------

export interface WasteBreakdownRow {
  reason: string;
  location: Location;
  qty: number;
  events: number;
}

export async function loadWasteBreakdown(opts: { from?: string; to?: string } = {}):
  Promise<{ from: string; to: string; rows: WasteBreakdownRow[] }> {
  const qs = new URLSearchParams();
  if (opts.from) qs.set('from', opts.from);
  if (opts.to) qs.set('to', opts.to);
  const path = '/inventory/waste-breakdown' + (qs.toString() ? `?${qs}` : '');
  return request(path);
}

// ---------- inventory · per-product trend (Phase 2b) ------------------------

export interface TrendRow {
  day: string;                          // YYYY-MM-DD (UTC)
  kind: 'produced' | 'sold' | 'wasted';
  qty: number;                          // positive
}

export async function loadProductTrend(
  productId: string, days: number = 14
): Promise<{ product_id: string; days: number; rows: TrendRow[] }> {
  const qs = new URLSearchParams({ product_id: productId, days: String(days) });
  return request(`/inventory/trend?${qs}`);
}

// ---------- end inventory ----------

// ---------- 人事档案 (HR records) -------------------------------------------

export type HrStore = 'RH' | 'DT' | 'NY';
export type HrStatus = '在职' | '试用' | '休假' | '离职';

export interface HrRecord {
  id: string;
  name: string;
  staffId: string | null;
  status: HrStatus;
  hireDate: string | null;            // YYYY or YYYY-MM
  progress: string | null;            // 进度 (skills)
  aspiration: string | null;          // 个人意愿
  notes: string | null;               // 备注
  idLink: string | null;
  contractLink: string | null;
  shift: 'morning' | 'evening' | null;   // 员工早晚班 tag (matched against a 领班's shift)
  stores: HrStore[];
  restricted: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string | null;       // newest visible timeline entry time (list triage)
  lastActivityKind: HrTimelineKind | null;
  // Admin-only — absent entirely from a 店长's response (D2: omit).
  salaryAmount?: number | null;
  salaryType?: 'monthly' | 'hourly' | null;
  salaryNote?: string | null;
  paystubLink?: string | null;
}

export type HrTimelineKind = 'progress' | 'aspiration' | 'request' | 'decision' | 'note'; // 进度/个人意愿/诉求/公司决策/备注

export interface HrTimelineEntry {
  id: number;
  kind: HrTimelineKind;
  content: string;
  adminOnly: boolean;
  actorId: string | null;
  actorName: string | null;
  createdAt: string;
}

export interface HrRecordInput {
  name: string;
  staff_id?: string | null;
  status?: HrStatus;
  hire_date?: string | null;
  progress?: string | null;
  aspiration?: string | null;
  notes?: string | null;
  id_link?: string | null;
  contract_link?: string | null;
  shift?: 'morning' | 'evening' | null;   // 员工早晚班 tag
  stores?: HrStore[];            // optional — staff not tied to one store leave empty
  // admin-only (ignored server-side for 店长)
  salary_amount?: number | null;
  salary_type?: 'monthly' | 'hourly' | null;
  salary_note?: string | null;
  paystub_link?: string | null;
  // super-admin-only
  restricted?: boolean;
}

export async function listHrRecords(): Promise<HrRecord[]> {
  const data = await request<{ records: HrRecord[] }>('/hr/records');
  return data.records;
}

export async function getHrRecord(id: string): Promise<HrRecord> {
  const data = await request<{ record: HrRecord }>(`/hr/records/${encodeURIComponent(id)}`);
  return data.record;
}

export async function createHrRecord(input: HrRecordInput): Promise<{ id: string }> {
  return request('/hr/records', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateHrRecord(id: string, patch: Partial<HrRecordInput>): Promise<void> {
  await request(`/hr/records/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function deleteHrRecord(id: string): Promise<void> {
  await request(`/hr/records/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function listHrTimeline(recordId: string): Promise<HrTimelineEntry[]> {
  const data = await request<{ entries: HrTimelineEntry[] }>(
    `/hr/records/${encodeURIComponent(recordId)}/timeline`
  );
  return data.entries;
}

export async function addHrTimeline(
  recordId: string, kind: HrTimelineKind, content: string, adminOnly = false
): Promise<{ id: number }> {
  return request(`/hr/records/${encodeURIComponent(recordId)}/timeline`, {
    method: 'POST', body: JSON.stringify({ kind, content, admin_only: adminOnly })
  });
}

export async function deleteHrTimeline(recordId: string, entryId: number): Promise<void> {
  await request(`/hr/records/${encodeURIComponent(recordId)}/timeline/${entryId}`, { method: 'DELETE' });
}

// ---------- HR documents (real file upload/download via R2) ----------

export type HrDocKind = 'id' | 'contract' | 'paystub';   // 身份证 / 合同 / 工资单

export interface HrDocument {
  id: string;
  recordId: string;
  kind: HrDocKind;
  filename: string;
  sizeBytes: number;
  mime: string;
  uploadedBy: string | null;
  uploadedAt: string;
}

export async function listHrDocuments(recordId: string): Promise<HrDocument[]> {
  const data = await request<{ documents: HrDocument[] }>(
    `/hr/records/${encodeURIComponent(recordId)}/documents`
  );
  return data.documents;
}

export async function uploadHrDocument(
  recordId: string, kind: HrDocKind, file: File
): Promise<{ document: HrDocument }> {
  const token = getToken();
  const fd = new FormData();
  fd.append('kind', kind);
  fd.append('file', file);
  const res = await fetch(`${API_BASE}/hr/records/${encodeURIComponent(recordId)}/documents`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},   // no Content-Type — browser sets multipart boundary
    body: fd
  });
  let payload: unknown;
  try { payload = await res.json(); } catch { payload = null; }
  if (!res.ok) {
    const msg = (payload && typeof payload === 'object' && 'error' in payload)
      ? String((payload as { error: unknown }).error) : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, payload, msg);
  }
  return payload as { document: HrDocument };
}

export async function downloadHrDocument(docId: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/hr/documents/${encodeURIComponent(docId)}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { const p = await res.json(); if (p && typeof p === 'object' && 'error' in p) msg = String((p as { error: unknown }).error); } catch { /* ok */ }
    throw new ApiError(res.status, null, msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export async function deleteHrDocument(docId: string): Promise<void> {
  await request(`/hr/documents/${encodeURIComponent(docId)}`, { method: 'DELETE' });
}

// ---------- end HR ----------

export async function downloadAttachment(id: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/attachments/${encodeURIComponent(id)}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { const p = await res.json(); if (p && typeof p === 'object' && 'error' in p) msg = String((p as { error: unknown }).error); } catch { /* ok */ }
    throw new ApiError(res.status, null, msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
